/**
 * Slider testimonial (solo mobile ≤991.98px).
 * - .section-testimonials (snippet homepage)
 * - .store-testimonials (snippet store)
 * Nessuna modifica ai template PHP.
 */
import Swiper from "./swiper-init.js";

const MOBILE_MQ = "(max-width: 991.98px)";

const TARGETS = [
    {
        sectionSelector: ".section-testimonials",
        gridSelector: ":scope > .row",
        isSlide: function (el) {
            return !!el.querySelector(".cf-review-card");
        },
        sliderClass: "section-testimonials__slider",
        controlsClass: "section-testimonials__slider-controls",
        activeClass: "is-testimonials-slider",
    },
    {
        sectionSelector: ".store-testimonials",
        gridSelector: ":scope > .store-testimonials__grid",
        isSlide: function (el) {
            return el.classList.contains("store-testimonials__card");
        },
        sliderClass: "store-testimonials__slider",
        controlsClass: "store-testimonials__slider-controls",
        activeClass: "is-store-testimonials-slider",
    },
];

const instances = new WeakMap();
let sharedMq = null;

function getSharedMq() {
    if (!sharedMq) {
        sharedMq = window.matchMedia(MOBILE_MQ);
    }

    return sharedMq;
}

function isConcreteEditing() {
    return !!document.getElementById("ccm-menu-click-proxy");
}

function createSliderControls(controlsClass) {
    const controls = document.createElement("div");
    controls.className = controlsClass + " swiper-controls";

    const pagination = document.createElement("div");
    pagination.className = "swiper-pagination";
    controls.appendChild(pagination);

    return controls;
}

class TestimonialsMobileSlider {
    constructor(section, mq, config) {
        this.section = section;
        this.mq = mq;
        this.config = config;
        this.grid = section.querySelector(config.gridSelector);
        this.swiper = null;
        this.sliderEl = null;
        this.controlsEl = null;
        this.equalizeTimer = null;
        this.scheduleEqualize = this.scheduleEqualizeSlideHeights.bind(this);
        this.onWindowResize = this.scheduleEqualize;

        this.onMqChange = this.onMqChange.bind(this);
        mq.addEventListener("change", this.onMqChange);
        this.onMqChange();
    }

    getSlides() {
        if (!this.swiper || !this.swiper.slides) {
            return [];
        }

        return Array.from(this.swiper.slides);
    }

    clearSlideHeights() {
        this.getSlides().forEach(function (slide) {
            slide.style.minHeight = "";
            slide.style.height = "";
        });
    }

    equalizeSlideHeights() {
        if (!this.swiper) {
            return;
        }

        const slides = this.getSlides();

        if (slides.length === 0) {
            return;
        }

        slides.forEach(function (slide) {
            slide.style.minHeight = "";
            slide.style.height = "";
        });

        let maxHeight = 0;

        slides.forEach(function (slide) {
            maxHeight = Math.max(maxHeight, slide.offsetHeight);
        });

        if (maxHeight <= 0) {
            return;
        }

        slides.forEach(function (slide) {
            slide.style.minHeight = maxHeight + "px";
        });

        this.swiper.update();
        if (typeof this.swiper.updateAutoHeight === "function") {
            this.swiper.updateAutoHeight();
        }
    }

    scheduleEqualizeSlideHeights() {
        if (this.equalizeTimer) {
            window.clearTimeout(this.equalizeTimer);
        }

        this.equalizeTimer = window.setTimeout(
            function () {
                this.equalizeTimer = null;
                window.requestAnimationFrame(
                    function () {
                        this.equalizeSlideHeights();
                    }.bind(this)
                );
            }.bind(this),
            50
        );
    }

    bindEqualizeEvents() {
        if (!this.swiper) {
            return;
        }

        this.swiper.on("resize", this.scheduleEqualize);
        this.swiper.on("observerUpdate", this.scheduleEqualize);
        window.addEventListener("resize", this.onWindowResize, { passive: true });
        this.scheduleEqualize();
    }

    unbindEqualizeEvents() {
        if (this.equalizeTimer) {
            window.clearTimeout(this.equalizeTimer);
            this.equalizeTimer = null;
        }

        window.removeEventListener("resize", this.onWindowResize);

        if (this.swiper) {
            this.swiper.off("resize", this.scheduleEqualize);
            this.swiper.off("observerUpdate", this.scheduleEqualize);
        }
    }

    onMqChange() {
        if (this.mq.matches) {
            this.enable();
        } else {
            this.disable();
        }
    }

    enable() {
        if (
            this.swiper ||
            !this.grid ||
            isConcreteEditing()
        ) {
            return;
        }

        const slides = Array.from(this.grid.children).filter(
            this.config.isSlide
        );

        if (slides.length === 0) {
            return;
        }

        slides.forEach(function (slide) {
            slide.classList.add("swiper-slide");
        });

        this.sliderEl = document.createElement("div");
        this.sliderEl.className = this.config.sliderClass + " swiper";

        this.grid.parentNode.insertBefore(this.sliderEl, this.grid);
        this.sliderEl.appendChild(this.grid);
        this.grid.classList.add("swiper-wrapper");

        this.controlsEl = createSliderControls(this.config.controlsClass);
        this.sliderEl.appendChild(this.controlsEl);

        this.section.classList.add(this.config.activeClass);

        this.swiper = new Swiper(this.sliderEl, {
            slidesPerView: "auto",
            spaceBetween: 16,
            slidesOffsetAfter: 24,
            watchOverflow: true,
            observer: true,
            observeParents: true,
            autoHeight: true,
            pagination: {
                el: this.controlsEl.querySelector(".swiper-pagination"),
                clickable: true,
            },
        });

        this.bindEqualizeEvents();
    }

    disable() {
        if (!this.swiper && !this.sliderEl) {
            return;
        }

        this.unbindEqualizeEvents();

        if (this.swiper) {
            this.clearSlideHeights();
            this.swiper.destroy(true, true);
            this.swiper = null;
        }

        if (this.grid && this.sliderEl) {
            this.grid.classList.remove("swiper-wrapper");

            Array.from(this.grid.children).forEach(function (slide) {
                slide.classList.remove("swiper-slide");
                slide.style.minHeight = "";
                slide.style.height = "";
            });

            this.section.insertBefore(this.grid, this.sliderEl);
            this.sliderEl.remove();
            this.sliderEl = null;
            this.controlsEl = null;
        }

        this.section.classList.remove(this.config.activeClass);
    }
}

function collectTestimonialsSections(root, config) {
    const scope = root && root.nodeType === 1 ? root : document;
    const sections = [];
    const selector = config.sectionSelector;

    if (
        scope.matches &&
        scope.matches(selector) &&
        scope.querySelector(config.gridSelector)
    ) {
        const grid = scope.querySelector(config.gridSelector);
        if (Array.from(grid.children).some(config.isSlide)) {
            sections.push(scope);
        }
    }

    scope.querySelectorAll(selector).forEach(function (section) {
        const grid = section.querySelector(config.gridSelector);
        if (!grid) {
            return;
        }
        if (Array.from(grid.children).some(config.isSlide)) {
            sections.push(section);
        }
    });

    return sections;
}

function initTestimonialsMobileSlider(root) {
    if (isConcreteEditing()) {
        return;
    }

    const mq = getSharedMq();
    const seen = new Set();

    TARGETS.forEach(function (config) {
        collectTestimonialsSections(root, config).forEach(function (section) {
            if (seen.has(section)) {
                return;
            }

            seen.add(section);

            const existing = instances.get(section);
            if (existing) {
                if (
                    mq.matches &&
                    !section.classList.contains(config.activeClass)
                ) {
                    existing.enable();
                }
                return;
            }

            instances.set(
                section,
                new TestimonialsMobileSlider(section, mq, config)
            );
        });
    });
}

function bindTestimonialsSliderLifecycle(root) {
    initTestimonialsMobileSlider(root);
}

function scheduleBindRetries() {
    [0, 100, 400, 1200].forEach(function (delay) {
        window.setTimeout(function () {
            bindTestimonialsSliderLifecycle();
        }, delay);
    });
}

function observeLateTestimonials() {
    if (typeof MutationObserver === "undefined") {
        return;
    }

    let timer = null;

    const observer = new MutationObserver(function () {
        if (timer) {
            clearTimeout(timer);
        }

        timer = window.setTimeout(function () {
            timer = null;
            bindTestimonialsSliderLifecycle();
        }, 80);
    });

    observer.observe(document.documentElement, { childList: true, subtree: true });
}

function bindDocumentLifecycle() {
    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", function () {
            bindTestimonialsSliderLifecycle();
            scheduleBindRetries();
        });
    } else {
        bindTestimonialsSliderLifecycle();
        scheduleBindRetries();
    }

    observeLateTestimonials();
}

bindDocumentLifecycle();

window.__cfBindTestimonialsSlider = bindTestimonialsSliderLifecycle;

export function registerTestimonialsSliderWithJquery($) {
    $(function () {
        bindTestimonialsSliderLifecycle();
        scheduleBindRetries();
    });
    $(document).on("ajaxComplete", bindTestimonialsSliderLifecycle);
}

export { initTestimonialsMobileSlider, bindTestimonialsSliderLifecycle };
